import Alert from '@mui/material/Alert'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { useQuotesView } from 'context/QuotesViewContext'
import { analyticsPageView } from 'global/helpers/analytics'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { type ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QuoteFilters from './QuoteFilters'
import QuotePredefinedFilters from './QuotePredefinedFilters'
import {
  filteredQuotesQueryOptions,
  quoteRequestCountersQueryOptions,
} from './Quotes.service'
import QuoteTable from './QuoteTable'

const Quotes = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, page, restoreScrollPosition } = useQuotesView()
  const quotesQuery = useQuery(filteredQuotesQueryOptions(page, filters))
  const quoteRequestCountersQuery = useQuery(quoteRequestCountersQueryOptions)

  useEffect(() => {
    restoreScrollPosition()
    analyticsPageView('polestar/quotes_list')
  }, [])

  return (
    <Container maxWidth="xxl">
      {quotesQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.quotes.loading')}</Typography>
        </KNLoader>
      )}
      {quotesQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(quotesQuery.error)}</Alert>
      )}
      {quoteRequestCountersQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(quoteRequestCountersQuery.error)}</Alert>
      )}
      {quotesQuery.isSuccess && (
        <>
          {quoteRequestCountersQuery.isSuccess && (
            <QuotePredefinedFilters counters={quoteRequestCountersQuery.data} sx={{ mb: 2 }} />
          )}
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <QuoteFilters />
              <Typography variant="h6">
                {t('screens.cs.quotes.quotes_count', { count: quotesQuery.data.totalElements })}
              </Typography>
            </Stack>
            <QuoteTable
              quotes={quotesQuery.data.content}
              pageCount={quotesQuery.data.totalPages}
            />
          </Paper>
        </>
      )}
    </Container>
  )
}

export default Quotes

